/**
 * CTA Section Component
 *
 * Reusable call-to-action block with variant-based styling.
 * Used at the bottom of marketing pages to drive conversions. Buttons render through `<SmartLink>`
 * (LIFE-SHORTLISTED-001 F-P11-2), so one that leaves the site is a real link — a router `<Link to>`
 * had made it an in-app path that never loaded (a newsletter's "Join the list").
 *
 * @module @puffin-core/marketing-shell/CTASection
 */

import PropTypes from 'prop-types';

import { SmartLink } from '../links/SmartLink.jsx';

/**
 * CTASection Component
 *
 * Renders a call-to-action section with heading, optional subheading,
 * and primary/secondary action buttons.
 *
 * @param {Object} props - Component props
 * @param {string} [props.eyebrow] - Small uppercase kicker above the heading
 * @param {string} props.heading - CTA heading text
 * @param {string} [props.subheading] - Optional subheading text
 * @param {string} props.primaryAction - Primary button label
 * @param {string} props.primaryLink - Primary button destination
 * @param {string} [props.secondaryAction] - Secondary button label
 * @param {string} [props.secondaryLink] - Secondary button destination
 * @param {string} [props.variant='primary'] - Style variant (primary, dark, light)
 * @param {string} [props.id] - Optional id attribute on the section element.
 * @param {(href: string, destinationKind: string) => void} [props.onOutboundClick] - A click on a
 *   button that leaves the site (`<SmartLink onOutboundClick>`).
 * @returns {React.JSX.Element} CTA section
 */
const CTASection = ({
  eyebrow,
  heading,
  subheading,
  primaryAction,
  primaryLink,
  secondaryAction,
  secondaryLink,
  variant = 'primary',
  id,
  onOutboundClick,
}) => {
  const primaryBtnClass =
    variant === 'primary'
      ? 'btn btn-marketing-lg btn-marketing-white'
      : 'btn btn-marketing-lg btn-marketing-primary';

  const secondaryBtnClass =
    variant === 'primary'
      ? 'btn btn-marketing-lg btn-marketing-ghost-white'
      : 'btn btn-marketing-lg btn-marketing-secondary';

  const sectionExtras = id ? { id } : {};

  return (
    <section className={`cta-block cta-block-${variant}`} {...sectionExtras}>
      <div className="cta-content">
        {eyebrow ? <p className="text-overline">{eyebrow}</p> : null}
        <h2 className="cta-heading">{heading}</h2>
        {subheading && <p className="cta-subheading">{subheading}</p>}
      </div>
      <div className="cta-actions">
        <SmartLink href={primaryLink} className={primaryBtnClass} onOutboundClick={onOutboundClick}>
          {primaryAction}
        </SmartLink>
        {secondaryAction && secondaryLink && (
          <SmartLink
            href={secondaryLink}
            className={secondaryBtnClass}
            onOutboundClick={onOutboundClick}
          >
            {secondaryAction}
          </SmartLink>
        )}
      </div>
    </section>
  );
};

CTASection.propTypes = {
  eyebrow: PropTypes.string,
  heading: PropTypes.string.isRequired,
  subheading: PropTypes.string,
  primaryAction: PropTypes.string.isRequired,
  primaryLink: PropTypes.string.isRequired,
  secondaryAction: PropTypes.string,
  secondaryLink: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'dark', 'light']),
  id: PropTypes.string,
  onOutboundClick: PropTypes.func,
};

export default CTASection;
